// Phase 4 — Notification Center.
// Renders the per-user inbox sourced from `state.notifications`
// (which the Shell loads via api.listNotifications). On view we
// stamp the highest seen NotificationId into localStorage so the
// unread badge in the top nav clears.
//
// Server queues live in notify.NotificationQueue keyed by ToAddress
// (email). The "send" pipeline is decoupled — the in-app inbox just
// reads what's queued and displays it. See server/src/routes/notifications.js.

import React, { useEffect, useMemo } from "react";
import {
  Bell, Trophy, ClipboardList, Wine, Cookie, GlassWater,
  MessageCircle, Send, Megaphone, CheckCircle2
} from "lucide-react";
import { Panel } from "../components/primitives";

// localStorage key — also read by the nav bell badge so both stay in sync.
export const NOTIF_SEEN_KEY = "gabruz.notifications.lastSeenId";

// Convenience for other surfaces (TopBar, ShellV4) — returns the
// numeric ID of the last notification this user has acknowledged.
export function getLastSeenNotifId() {
  const raw = localStorage.getItem(NOTIF_SEEN_KEY);
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}
export function setLastSeenNotifId(id) {
  localStorage.setItem(NOTIF_SEEN_KEY, String(id));
  // Custom event so listeners (badge) refresh without polling.
  window.dispatchEvent(new CustomEvent("notif:seen", { detail: { id } }));
}

// Per-channel/topic icon — fallback to the generic Bell.
const ICON_MAP = [
  { match: /SQUAD|SELECTED|XI|ROSTER/i,                  Icon: ClipboardList, color: "var(--gold)" },
  { match: /MATCH RESULT|TOP PERFORMER|WON|LOST/i,       Icon: Trophy,        color: "var(--gold)" },
  { match: /DRINKS|VOLUNTEER FOR DRINKS|VOLUNTEERED/i,   Icon: Wine,          color: "var(--blue)" },
  { match: /SNACK/i,                                     Icon: Cookie,        color: "var(--orange)" },
  { match: /ALCOHOL|BEER/i,                              Icon: GlassWater,    color: "var(--orange)" },
  { match: /MESSAGE|CHAT|MENTION/i,                      Icon: MessageCircle, color: "var(--blue)" },
  { match: /ANNOUNCEMENT|UPDATE/i,                       Icon: Megaphone,     color: "var(--gold)" }
];
function iconFor(message) {
  for (const e of ICON_MAP) if (e.match.test(message || "")) return e;
  return { Icon: Bell, color: "var(--muted)" };
}

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1)    return "Just now";
  if (diffMin < 60)   return `${diffMin}m ago`;
  const diffHr  = Math.round(diffMin / 60);
  if (diffHr < 24)    return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7)    return `${diffDay}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function Notifications({ state, user }) {
  const items = useMemo(() => {
    return [...(state?.notifications || [])]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [state?.notifications]);

  // Stamp lastSeen on view so the nav badge clears.
  useEffect(() => {
    if (items.length === 0) return;
    const maxId = items.reduce((m, n) => Math.max(m, Number(n.id) || 0), 0);
    if (maxId > getLastSeenNotifId()) setLastSeenNotifId(maxId);
  }, [items]);

  const lastSeen = getLastSeenNotifId();

  return (
    <div className="stack">
      <div className="builderTop">
        <div>
          <p>Inbox · Squad picks, volunteer slots, match results</p>
          <h2>
            <Bell size={20} style={{verticalAlign:"text-top", marginRight:8}}/>
            Notification Center
          </h2>
        </div>
      </div>

      <Panel title={
        <>
          <Bell size={16}/> Your notifications
          <span style={{marginLeft:"auto", color:"var(--muted)", fontSize:12}}>
            {items.length} total
          </span>
        </>
      }>
        {items.length === 0 ? (
          <div className="notif-empty">
            <Bell size={36}/>
            <h3>No notifications yet</h3>
            <p>
              You'll see updates here when you're picked in a playing XI,
              when you volunteer for a game, or when match results are
              recorded.
            </p>
          </div>
        ) : (
          <ul className="notif-list">
            {items.map(n => {
              const { Icon, color } = iconFor(n.message);
              const isUnread = Number(n.id) > lastSeen;
              return (
                <li key={n.id} className={`notif-row${isUnread ? " notif-row--unread" : ""}`}>
                  <span className="notif-icon" style={{ color }}>
                    <Icon size={16}/>
                  </span>
                  <div className="notif-body">
                    <div className="notif-msg">{n.message}</div>
                    <div className="notif-meta">
                      <small>{fmtWhen(n.createdAt)}</small>
                      {n.type && <small className="notif-channel">{n.type}</small>}
                      {!isUnread && <small><CheckCircle2 size={10} style={{verticalAlign:"middle"}}/> Seen</small>}
                    </div>
                  </div>
                  {isUnread && <span className="notif-dot" title="Unread"/>}
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    </div>
  );
}
